'use client';

import { useEffect, useMemo, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import {
  RecoverForm,
  ResetForm,
  SignInForm,
  SignUpForm,
  UnlockForm,
} from './unified-auth-forms';

export type AuthView = 'sign-in' | 'sign-up' | 'recover' | 'reset' | 'unlock';

type UnifiedAuthViewProps = {
  initialView?: AuthView;
  resetToken?: string;
};

function getResetToken(pathname: string, queryToken: string | null) {
  if (queryToken) return queryToken;

  const match = pathname.match(/^\/reset\/([^/?#]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

export function UnifiedAuthView({
  initialView = 'sign-in',
  resetToken,
}: UnifiedAuthViewProps) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const detectedToken = useMemo(
    () => resetToken || getResetToken(pathname, searchParams.get('token')),
    [pathname, resetToken, searchParams]
  );
  const [view, setView] = useState<AuthView>(detectedToken ? 'reset' : initialView);

  useEffect(() => {
    if (detectedToken) setView('reset');
  }, [detectedToken]);

  const changeView = (nextView: AuthView) => {
    setView(nextView);
    if (view === 'reset' && nextView === 'sign-in') router.replace('/');
  };

  switch (view) {
    case 'sign-up':
      return <SignUpForm onChangeView={changeView} />;
    case 'recover':
      return <RecoverForm onChangeView={changeView} />;
    case 'reset':
      return <ResetForm token={detectedToken} onChangeView={changeView} />;
    case 'unlock':
      return <UnlockForm />;
    default:
      return <SignInForm onChangeView={changeView} />;
  }
}
